/**
 * Картинки во вложениях вопроса (agroai-6al.66): PNG / JPEG / WebP, до 3 штук,
 * до 5 МБ каждая. Уходят частями сообщения `file` (data URL) — как принимает
 * AI SDK; те же пределы проверяет сервер (`ai_agent/images.py`).
 */

import type { FileUIPart } from 'ai';
import { defineMessages } from '../i18n/define';
import { tr } from '../i18n/runtime';

const m = defineMessages({
  readFailed: { id: 'ai.attach.readFailed', defaultMessage: 'не удалось прочитать файл', description: 'Причина ошибки при чтении выбранного файла-картинки; строчными, показывается в тексте ошибки' },
  onlyTypes: { id: 'ai.attach.onlyTypes', defaultMessage: 'Картинки — только PNG, JPEG или WebP.', description: 'Ошибка под полем вопроса к ИИ: выбран файл неподходящего типа' },
  noName: { id: 'ai.attach.noName', defaultMessage: 'без имени', description: 'Вместо имени файла-картинки, когда у него нет имени; строчными, подставляется в кавычки' },
  tooBig: { id: 'ai.attach.tooBig', defaultMessage: 'Картинка «{name}» больше {mb} МБ.', description: 'Ошибка под полем вопроса к ИИ: картинка слишком большая; {name} — имя файла, {mb} — предел в мегабайтах' },
  tooMany: { id: 'ai.attach.tooMany', defaultMessage: 'Не больше {count, plural, one {# картинки} few {# картинок} many {# картинок} other {# картинок}} в одном вопросе.', description: 'Ошибка под полем вопроса к ИИ: слишком много картинок; # — предельное число' },
  screenshotName: { id: 'ai.attach.screenshotName', defaultMessage: 'скриншот-{n}.{ext}', description: 'Имя вставленной картинки без имени (скриншот из буфера); {n} — порядковый номер, {ext} — расширение файла (png)' },
});

export const MAX_IMAGES = 3;
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
export const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

export interface Attachment extends FileUIPart {
  id: string;
}

export function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error(tr(m.readFailed)));
    reader.readAsDataURL(file);
  });
}

/** Проверка пачки: что принять и почему остальное отклонено (словами). */
export function screen(files: File[], already: number): { accepted: File[]; problem: string | null } {
  const accepted: File[] = [];
  let problem: string | null = null;
  for (const file of files) {
    if (!IMAGE_TYPES.includes(file.type)) problem = tr(m.onlyTypes);
    else if (file.size > MAX_IMAGE_BYTES) problem = tr(m.tooBig, { name: file.name || tr(m.noName), mb: MAX_IMAGE_BYTES / (1024 * 1024) });
    else if (already + accepted.length >= MAX_IMAGES) problem = tr(m.tooMany, { count: MAX_IMAGES });
    else accepted.push(file);
  }
  return { accepted, problem };
}

export async function toAttachments(files: File[]): Promise<Attachment[]> {
  return Promise.all(
    files.map(async (file, index) => ({
      id: `${Date.now()}_${index}_${Math.random().toString(36).slice(2, 7)}`,
      type: 'file' as const,
      mediaType: file.type,
      filename: file.name || tr(m.screenshotName, { n: index + 1, ext: file.type.split('/')[1] ?? 'png' }),
      url: await readAsDataUrl(file),
    })),
  );
}
